import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Star } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { name: "", email: "", role: "", rating: 5, message: "", consent_to_publish: true };

export const FeedbackForm = () => {
  const [form, setForm] = useState(initial);
  const [hoverStar, setHoverStar] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.message || !form.rating) {
      setError("Please share your name, a rating and a few words.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        ...form,
        email: form.email || null,
        rating: Number(form.rating),
      });
      setSubmitted(true);
      setForm(initial);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#E6DFD3] py-2.5 text-[#2D231E] font-sans text-sm placeholder-[#A88B6C] focus:outline-none focus:border-[#4A3C31] transition-colors rounded-none";
  const labelCls =
    "block text-[10px] font-sans tracking-[0.25em] uppercase text-[#5D4037] mb-1.5";

  if (submitted) {
    return (
      <div data-testid="feedback-success" className="bg-[#F9F6F0] border border-[#E6DFD3] p-8 sm:p-10 rounded-sm flex flex-col items-start gap-3">
        <CheckCircle2 size={28} strokeWidth={1.5} className="text-[#8B7355]" />
        <h4 className="font-serif text-2xl text-[#2D231E]">Thank you.</h4>
        <p className="font-sans text-sm text-[#5D4037] max-w-md">
          We've received your feedback — it genuinely means a lot. We'll read every word.
        </p>
        <button type="button" onClick={() => setSubmitted(false)}
          className="mt-2 inline-flex items-center justify-center px-5 py-2.5 bg-transparent border border-[#4A3C31] text-[#4A3C31] font-sans text-[10px] tracking-[0.25em] uppercase hover:bg-[#4A3C31] hover:text-[#F9F6F0] transition-colors rounded-sm">
          Share more
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="feedback-form"
      onSubmit={onSubmit}
      className="bg-[#F9F6F0] border border-[#E6DFD3] p-7 sm:p-9 rounded-sm"
    >
      <p className="font-sans text-xs tracking-[0.35em] uppercase text-[#8B7355] mb-2">Share your experience</p>
      <h3 className="font-serif text-2xl sm:text-3xl text-[#2D231E] mb-2">Leave us a few words.</h3>
      <p className="font-sans text-sm text-[#5D4037] max-w-lg mb-7">
        Spent time at WorkSpaces? Tell us how it went — what worked, what we
        could do better. With your permission, we may use your review on this page.
      </p>

      {/* star rating */}
      <div className="mb-7">
        <p className={labelCls}>How was it?</p>
        <div className="flex items-center gap-1.5" data-testid="feedback-stars">
          {[1,2,3,4,5].map((n) => {
            const active = (hoverStar || form.rating) >= n;
            return (
              <button
                key={n} type="button"
                onMouseEnter={() => setHoverStar(n)}
                onMouseLeave={() => setHoverStar(0)}
                onClick={() => setForm({ ...form, rating: n })}
                aria-label={`${n} star${n>1?'s':''}`}
                className="p-1"
              >
                <Star
                  size={26} strokeWidth={1.5}
                  className={`transition-colors ${active ? "text-[#8B7355] fill-[#8B7355]" : "text-[#C4A484]"}`}
                />
              </button>
            );
          })}
          <span className="ml-3 font-sans text-xs text-[#8B7355]">{form.rating} / 5</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
        <div>
          <label className={labelCls} htmlFor="fb-name">Name</label>
          <input id="fb-name" name="name" value={form.name} onChange={onChange} required placeholder="Your name" data-testid="feedback-name" className={inputCls} />
        </div>
        <div>
          <label className={labelCls} htmlFor="fb-role">Role / business (optional)</label>
          <input id="fb-role" name="role" value={form.role} onChange={onChange} placeholder="e.g. small business owner" data-testid="feedback-role" className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="fb-email">Email (optional)</label>
          <input id="fb-email" name="email" type="email" value={form.email} onChange={onChange} placeholder="you@example.com" data-testid="feedback-email" className={inputCls} />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="fb-message">Your feedback</label>
          <textarea id="fb-message" name="message" rows={4} value={form.message} onChange={onChange} required placeholder="A few words about your experience…" data-testid="feedback-message" className={`${inputCls} resize-none`} />
        </div>
        <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer">
          <input type="checkbox" name="consent_to_publish" checked={form.consent_to_publish} onChange={onChange} data-testid="feedback-consent" className="mt-1 accent-[#4A3C31]" />
          <span className="font-sans text-xs text-[#5D4037]">
            You may use my first name and review on this website. (Uncheck if you'd rather keep it private — we'll still read it.)
          </span>
        </label>
      </div>

      {error && <p data-testid="feedback-error" className="mt-5 font-sans text-sm text-[#9D5B5B]">{error}</p>}

      <div className="mt-7 flex justify-end">
        <button type="submit" disabled={submitting} data-testid="feedback-submit"
          className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#4A3C31] text-[#F9F6F0] font-sans text-[11px] tracking-[0.3em] uppercase hover:bg-[#2D231E] transition-colors rounded-sm disabled:opacity-60">
          {submitting && <Loader2 size={12} className="animate-spin" />}
          {submitting ? "Sending…" : "Share Feedback"}
        </button>
      </div>
    </form>
  );
};
